import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, Trash2Icon, ImageIcon, CheckCircleIcon } from 'lucide-react';
interface Option {
  id: string;
  name: string;
  values: string;
}
interface Modifier {
  id: string;
  name: string;
  price: number;
}
export function ProductEdit() {
  const navigate = useNavigate();
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const [params] = useSearchParams();
  const shopId = params.get('shop') || '';
  const isNew = productId === 'new';
  const [name, setName] = useState(isNew ? '' : 'Swim Shorts');
  const [description, setDescription] = useState(isNew ? '' : 'Quick-dry swim shorts for men, made from recycled polyester.');
  const [sku, setSku] = useState(isNew ? '' : 'SS-003');
  const [category, setCategory] = useState(isNew ? 'Swimwear' : 'Swimwear');
  const [price, setPrice] = useState(isNew ? '0.00' : '34.99');
  const [salePrice, setSalePrice] = useState(isNew ? '' : '29.99');
  const [stockQuantity, setStockQuantity] = useState(isNew ? '0' : '28');
  const [imageUrl, setImageUrl] = useState(isNew ? '' : 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80');
  const [isActive, setIsActive] = useState(true);
  const [options, setOptions] = useState<Option[]>(isNew ? [] : [{
    id: 'o1',
    name: 'Size',
    values: 'S, M, L, XL'
  }, {
    id: 'o2',
    name: 'Color',
    values: 'Navy, Coral, Black'
  }]);
  const [modifiers, setModifiers] = useState<Modifier[]>(isNew ? [] : [{
    id: 'm1',
    name: 'Express delivery',
    price: 5.0
  }]);
  const [savedToast, setSavedToast] = useState(false);
  const addOption = () => setOptions([...options, {
    id: 'o' + Math.random().toString(36).slice(2, 7),
    name: '',
    values: ''
  }]);
  const removeOption = (id: string) => setOptions(options.filter(o => o.id !== id));
  const addModifier = () => setModifiers([...modifiers, {
    id: 'm' + Math.random().toString(36).slice(2, 7),
    name: '',
    price: 0
  }]);
  const removeModifier = (id: string) => setModifiers(modifiers.filter(m => m.id !== id));
  const handleSave = () => {
    setSavedToast(true);
    setTimeout(() => {
      setSavedToast(false);
      navigate(shopId ? `/shop-details?id=${shopId}` : '/shops');
    }, 1200);
  };
  return <div className="max-w-[1200px] mx-auto space-y-6" data-id="element-11963">
      <div data-id="element-11964">
        <button onClick={() => navigate(shopId ? `/shop-details?id=${shopId}` : '/shops')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 mb-3" data-id="element-11965">
          <ArrowLeftIcon size={14} className="mr-1.5" data-id="element-11966" />
          Back to shop
        </button>
        <div className="flex items-center justify-between" data-id="element-11967">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-11968">
            {isNew ? 'New product' : 'Edit product'}
          </h1>
          <div className="flex items-center gap-2" data-id="element-11969">
            <button onClick={() => navigate(-1)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-11970">
              Cancel
            </button>
            <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm" data-id="element-11971">
              Save product
            </button>
          </div>
        </div>
      </div>

      {savedToast && <div className="fixed bottom-6 right-6 bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm shadow-lg flex items-center gap-2 z-50" data-id="element-11972">
          <CheckCircleIcon size={16} className="text-green-400" data-id="element-11973" />
          Product saved
        </div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-11974">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6" data-id="element-11975">
          {/* Basic info */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-11976">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11977">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-11978">
                Basic information
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4" data-id="element-11979">
              <div data-id="element-11980">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11981">
                  Name <span className="text-red-400" data-id="element-11982">*</span>
                </label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11983" />
              </div>
              <div data-id="element-11984">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11985">
                  Description
                </label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11986" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-11987">
                <div data-id="element-11988">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11989">
                    SKU
                  </label>
                  <input type="text" value={sku} onChange={e => setSku(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11990" />
                </div>
                <div data-id="element-11991">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11992">
                    Category
                  </label>
                  <input type="text" value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11993" />
                </div>
              </div>
            </div>
          </div>

          {/* Pricing & inventory */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-11994">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11995">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-11996">
                Pricing & inventory
              </h2>
            </div>
            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-11997">
              <div data-id="element-11998">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11999">
                  Regular price (MYR) <span className="text-red-400" data-id="element-12000">*</span>
                </label>
                <input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12001" />
              </div>
              <div data-id="element-12002">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12003">
                  Sale price (MYR)
                </label>
                <input type="number" step="0.01" value={salePrice} onChange={e => setSalePrice(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12004" />
                <p className="text-xs text-gray-400 mt-1" data-id="element-12005">
                  Leave blank for no discount
                </p>
              </div>
              <div data-id="element-12006">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12007">
                  Stock quantity
                </label>
                <input type="number" value={stockQuantity} onChange={e => setStockQuantity(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12008" />
              </div>
            </div>
          </div>

          {/* Product options */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12009">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-12010">
              <div data-id="element-12011">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12012">
                  Product options
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-12013">
                  Variants the customer must pick from, e.g. Size or Color.
                </p>
              </div>
              <button onClick={addOption} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-12014">
                <PlusIcon size={14} data-id="element-12015" />
                Add option
              </button>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-12016">
              {options.map((opt, i) => <div key={opt.id} className="border border-gray-200 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-3 items-end" data-id="element-12017">
                  <div data-id="element-12018">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12019">
                      Option name
                    </label>
                    <input type="text" value={opt.name} onChange={e => setOptions(options.map(o => o.id === opt.id ? {
                  ...o,
                  name: e.target.value
                } : o))} placeholder="e.g. Size" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12020" />
                  </div>
                  <div data-id="element-12021">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12022">
                      Values (comma separated)
                    </label>
                    <input type="text" value={opt.values} onChange={e => setOptions(options.map(o => o.id === opt.id ? {
                  ...o,
                  values: e.target.value
                } : o))} placeholder="e.g. S, M, L, XL" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12023" />
                  </div>
                  <button onClick={() => removeOption(opt.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Remove option" data-id="element-12024">
                    <Trash2Icon size={16} data-id="element-12025" />
                  </button>
                </div>)}
              {options.length === 0 && <p className="text-sm text-gray-500 text-center py-3" data-id="element-12026">
                  No options yet — add one to let customers pick a variant.
                </p>}
            </div>
          </div>

          {/* Modifiers */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12027">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-12028">
              <div data-id="element-12029">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12030">
                  Modifiers
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-12031">
                  Optional add-ons with their own price (e.g. gift wrap,
                  monogram).
                </p>
              </div>
              <button onClick={addModifier} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-12032">
                <PlusIcon size={14} data-id="element-12033" />
                Add modifier
              </button>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-12034">
              {modifiers.map(m => <div key={m.id} className="border border-gray-200 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto] gap-3 items-end" data-id="element-12035">
                  <div data-id="element-12036">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12037">
                      Modifier name
                    </label>
                    <input type="text" value={m.name} onChange={e => setModifiers(modifiers.map(x => x.id === m.id ? {
                  ...x,
                  name: e.target.value
                } : x))} placeholder="e.g. Gift wrap" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12038" />
                  </div>
                  <div data-id="element-12039">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12040">
                      Extra price (MYR)
                    </label>
                    <input type="number" step="0.01" value={m.price} onChange={e => setModifiers(modifiers.map(x => x.id === m.id ? {
                  ...x,
                  price: Number(e.target.value)
                } : x))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12041" />
                  </div>
                  <button onClick={() => removeModifier(m.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Remove modifier" data-id="element-12042">
                    <Trash2Icon size={16} data-id="element-12043" />
                  </button>
                </div>)}
              {modifiers.length === 0 && <p className="text-sm text-gray-500 text-center py-3" data-id="element-12044">
                  No modifiers yet — add one to offer paid extras.
                </p>}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6" data-id="element-12045">
          {/* Image */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12046">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12047">
              <ImageIcon size={16} className="text-gray-500" data-id="element-12048" />
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12049">
                Product image
              </h2>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-12050">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200" data-id="element-12051">
                {imageUrl ? <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" data-id="element-12052" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-12053">
                    <ImageIcon size={32} data-id="element-12054" />
                  </div>}
              </div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12055">
                Image URL
              </label>
              <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12056" />
            </div>
          </div>

          {/* Visibility */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12057">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12058">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12059">
                Visibility
              </h2>
            </div>
            <div className="px-6 py-5" data-id="element-12060">
              <label className="flex items-center cursor-pointer" data-id="element-12061">
                <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="sr-only" data-id="element-12062" />
                <div className={`relative w-9 h-5 rounded-full transition-colors ${isActive ? 'bg-[#E2231A]' : 'bg-gray-200'}`} data-id="element-12063">
                  <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${isActive ? 'translate-x-4' : ''}`} data-id="element-12064" />
                </div>
                <span className="ml-3 text-sm text-gray-700" data-id="element-12065">
                  {isActive ? 'Active — visible in shop' : 'Inactive — hidden from shop'}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>;
}